import type { OgAccountTraits, OgVisitorTraits } from '@opengov/analytics';
import type { ISessionV3 } from '@opengov/app-core/types';
import type { Entity } from '@opengov/bnp-core';
import { useEffect } from 'react';
import { useOgAnalytics } from './ogAnalyticsClient';

type OgIdentityConfig = {
  session?: ISessionV3;
  entity?: Entity;
};

const INTERNAL_EMAIL_DOMAIN = '@opengov.com';

/**
 * Visitor traits from the session and, once it has loaded, the routed entity. Only keys declared on
 * `OgVisitorTraits` appear: an unknown key is refused, and with `requireIdentity` a refused identity
 * would hold every event forever.
 */
export function buildVisitorTraits(session: ISessionV3, entity?: Entity): OgVisitorTraits {
  const fullName = [session.first_name, session.last_name].filter(Boolean).join(' ');
  return {
    ...(session.email && { og_email: session.email }),
    ...(fullName && { og_full_name: fullName }),
    ...(session.first_name && { og_first_name: session.first_name }),
    ...(session.last_name && { og_last_name: session.last_name }),
    ...(session.department && { og_department: session.department }),
    // The entity UUID — the same value passed to `group()` — so the trait joins to the account.
    ...(entity?.uuid && { og_entity_id: entity.uuid }),
    ...(entity?.subdomain && { og_subdomain: entity.subdomain }),
    // `ISessionV3` types email as a string, but some accounts (e.g. service or SSO users) have none at runtime.
    og_internal: Boolean(session.email) && session.email.endsWith(INTERNAL_EMAIL_DOMAIN)
  };
}

/** Account traits from the bnp-core `Entity`; only fields that exist on it are sent. */
export function buildAccountTraits(entity: Entity): OgAccountTraits {
  return {
    ...(entity.name && { og_account_name: entity.name }),
    ...(entity.subdomain && { og_subdomain: entity.subdomain }),
    ...(entity.salesforce_id && { og_salesforceid: entity.salesforce_id }),
    ...(entity.country && { og_country: entity.country }),
    ...(entity.state && { og_region: entity.state })
  };
}

/**
 * Tells the analytics client who the user is — but only in the runtime where that is
 * reporting-app's job.
 *
 * Identity is single-writer: standalone, reporting-app is the whole page and must identify. Mounted
 * in PSP (`shell`) or framed (`embedded`), the shell already identified the user, so calling
 * `identify` here would create a second identity for the same person. The no-op client (`disabled`)
 * is excluded by the same check, which covers the build and flag gates.
 *
 * The visitor id is `session.uuid` — the platform user uuid, the same id PSP keys its visitor on.
 *
 * Waits for the routed entity as well as the session. With `requireIdentity`, `identify()` is what
 * releases every held event, so identifying before the entity is known would send the first
 * `$pageview` and `signed_in` with no account. `group()` is called in the same turn, right after
 * `identify()`: the SDK releases held events in a microtask, so they go out with the account.
 */
export function useInitializeOgAnalytics({ session, entity }: OgIdentityConfig): void {
  const analytics = useOgAnalytics();

  useEffect(() => {
    if (analytics.mode !== 'standalone') {
      return;
    }
    if (!session?.uuid || !entity?.uuid) {
      return;
    }
    analytics.identify(session.uuid, buildVisitorTraits(session, entity));
    analytics.group(entity.uuid, buildAccountTraits(entity));
  }, [analytics, session, entity]);
}
